import {
  ChevronRight,
  Download,
  File,
  FileText,
  FolderOpen,
  FolderPlus,
  Grid,
  Home,
  Image,
  List,
  MoreVertical,
  Plus,
  RefreshCw,
  Share2,
  Trash2,
  Upload,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SearchableDropdown } from '../components/forms';
import Pagination from '../components/Pagination';
import { EmptyState, ModuleLayout } from '../components/ui';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import fileService from '../services/fileService';
import uploadService from '../services/uploadService';
import { FileCategory, FileQuery, FolderTreeNode, ProjectFile, ProjectFolder } from '../types';

type ViewMode = 'grid' | 'list';

const Files: React.FC = () => {
  const projectId = useCurrentProjectId();
  const [folders, setFolders] = useState<ProjectFolder[]>([]);
  const [_folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState<ProjectFolder | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<ProjectFolder[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);

  // Filters
  const [filters, setFilters] = useState<FileQuery>({
    search: '',
    category: undefined,
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    limit: 20,
  });

  // Stats
  const [stats, setStats] = useState({
    totalFiles: 0,
    totalFolders: 0,
    totalSize: 0,
  });

  // Load folders and files
  const loadData = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      // Load folders
      const foldersResult = await fileService.getFolders(projectId);
      if (foldersResult.success) {
        setFolders(foldersResult.folders);
        setStats(prev => ({ ...prev, totalFolders: foldersResult.folders.length }));
      }

      // Load folder tree
      const treeResult = await fileService.getFolderTree(projectId);
      if (treeResult.success) {
        setFolderTree(treeResult.tree);
      }

      // Load files
      const query: FileQuery = {
        ...filters,
        folder_id: currentFolder?._id,
        page: currentPage,
      };
      const filesResult = await fileService.getFiles(projectId, query);
      if (filesResult.success) {
        setFiles(filesResult.files);
        setTotalPages(filesResult.totalPages);
        setTotalFiles(filesResult.total);
        setStats(prev => ({ ...prev, totalFiles: filesResult.total }));
      }
    } catch (error) {
      console.error('Failed to load files:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, currentFolder, currentPage, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle folder navigation
  const navigateToFolder = (folder: ProjectFolder | null) => {
    setCurrentFolder(folder);
    setCurrentPage(1);
    setSelectedFiles([]);

    // Update breadcrumbs
    if (!folder) {
      setBreadcrumbs([]);
    } else {
      // Find path to folder
      const path: ProjectFolder[] = [];
      let current = folder;
      while (current) {
        path.unshift(current);
        current = folders.find(f => f._id === current?.parent_id) || null;
      }
      setBreadcrumbs(path);
    }
  };

  // Handle file upload
  const handleFileUpload = async (uploadedFiles: FileList | File[]) => {
    if (!projectId) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const fileArray = Array.from(uploadedFiles);
      const totalFiles = fileArray.length;
      let completed = 0;

      for (const file of fileArray) {
        const result = await uploadService.uploadFile(file, {
          module: 'files',
          projectId,
          onProgress: progress => {
            const overallProgress = ((completed + progress / 100) / totalFiles) * 100;
            setUploadProgress(Math.round(overallProgress));
          },
        });

        if (result.success && result.data) {
          // Create file record
          const uploadData = result.data as { key: string; [key: string]: unknown };
          await fileService.createFile(
            projectId,
            {
              key: uploadData.key,
              name: file.name,
              size: file.size,
              type: file.type,
            },
            {
              folder_id: currentFolder?._id,
              category: 'other',
            }
          );
        }
        completed++;
      }

      setShowUploadDrawer(false);
      loadData();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle file deletion
  const handleDeleteFiles = async () => {
    if (!projectId || selectedFiles.length === 0) return;

    if (window.confirm(`Delete ${selectedFiles.length} file(s)?`)) {
      const result = await fileService.bulkDeleteFiles(projectId, selectedFiles);
      if (result.success) {
        setSelectedFiles([]);
        loadData();
      }
    }
  };

  // Handle file download
  const handleDownload = async (file: ProjectFile) => {
    if (!projectId) return;
    await fileService.downloadFile(projectId, file._id, file.original_name);
  };

  // Handle filter change
  const handleFilterChange = (key: keyof FileQuery, value: FileQuery[keyof FileQuery]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Toggle file selection
  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };

  // Get file icon based on type
  const getFileIcon = (file: ProjectFile) => {
    const ext = file.file_extension?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) {
      return <Image className="h-5 w-5 text-blue-500" />;
    }
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
      return <FileText className="h-5 w-5 text-red-500" />;
    }
    return <File className="h-5 w-5 text-gray-500" />;
  };

  // Calculate stats
  const fileStats = [
    {
      title: 'Total Files',
      value: stats.totalFiles,
      icon: <File className="h-4 w-4" />,
      variant: 'brand' as const,
    },
    {
      title: 'Folders',
      value: stats.totalFolders,
      icon: <FolderOpen className="h-4 w-4" />,
      variant: 'info' as const,
    },
  ];

  // Category options
  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...fileService.getFileCategories().map(c => ({ value: c.value, label: c.label })),
  ];

  // Sort options
  const sortOptions = fileService.getSortOptions().map(s => ({
    value: s.value,
    label: s.label,
  }));

  return (
    <>
      <ModuleLayout
        title={projectId ? 'Project Files' : 'All Files'}
        description={
          projectId
            ? 'Manage documents and files for this project'
            : 'Organize and manage files across all projects'
        }
        icon={<FolderOpen className="h-4 w-4 text-brand-600" />}
        primaryAction={{
          label: 'Upload File',
          onClick: () => setShowUploadDrawer(true),
          icon: <Plus className="h-4 w-4 mr-2" />,
        }}
        onRefresh={loadData}
        isLoading={loading}
        additionalActions={
          <button
            onClick={() => setShowCreateFolderModal(true)}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            New Folder
          </button>
        }
        stats={fileStats}
        searchValue={filters.search || ''}
        onSearchChange={value => handleFilterChange('search', value)}
        filters={
          <div className="flex items-center gap-2">
            <SearchableDropdown
              placeholder="Category"
              value={filters.category || ''}
              onChange={value => handleFilterChange('category', value as FileCategory)}
              options={categoryOptions}
            />
            <SearchableDropdown
              placeholder="Sort By"
              value={filters.sort_by || 'created_at'}
              onChange={value => handleFilterChange('sort_by', value)}
              options={sortOptions}
            />
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
              >
                <List className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
              >
                <Grid className="h-4 w-4" />
              </button>
            </div>
          </div>
        }
      >
        {/* Breadcrumbs */}
        <div className="flex items-center space-x-2 mb-4 text-sm">
          <button
            onClick={() => navigateToFolder(null)}
            className="flex items-center text-gray-600 hover:text-brand-600"
          >
            <Home className="h-4 w-4 mr-1" />
            Root
          </button>
          {breadcrumbs.map((folder, index) => (
            <React.Fragment key={folder._id}>
              <ChevronRight className="h-4 w-4 text-gray-400" />
              <button
                onClick={() => navigateToFolder(folder)}
                className={`hover:text-brand-600 ${
                  index === breadcrumbs.length - 1 ? 'text-brand-600 font-medium' : 'text-gray-600'
                }`}
              >
                {folder.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Selected Files Actions */}
        {selectedFiles.length > 0 && (
          <div className="flex items-center justify-between bg-brand-50 border border-brand-200 rounded-lg px-4 py-2 mb-4">
            <span className="text-sm text-brand-700">{selectedFiles.length} file(s) selected</span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDeleteFiles}
                className="flex items-center px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        ) : files.length === 0 &&
          folders.filter(f => f.parent_id === currentFolder?._id).length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title="No files found"
            description="Upload files to get started or create a folder to organize your documents."
            action={{
              label: 'Upload File',
              onClick: () => setShowUploadDrawer(true),
            }}
          />
        ) : viewMode === 'list' ? (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedFiles(files.map(f => f._id));
                        } else {
                          setSelectedFiles([]);
                        }
                      }}
                      checked={selectedFiles.length === files.length && files.length > 0}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Modified
                  </th>
                  <th className="w-20 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Folders */}
                {folders
                  .filter(f => f.parent_id === (currentFolder?._id || null))
                  .map(folder => (
                    <tr
                      key={folder._id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigateToFolder(folder)}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <FolderOpen className="h-5 w-5 text-yellow-500 mr-3" />
                          <span className="font-medium">{folder.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500">Folder</td>
                      <td className="px-4 py-3 text-gray-500">{folder.file_count} items</td>
                      <td className="px-4 py-3 text-gray-500">
                        {new Date(folder.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                {/* Files */}
                {files.map(file => (
                  <tr key={file._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={selectedFiles.includes(file._id)}
                        onChange={() => toggleFileSelection(file._id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        {getFileIcon(file)}
                        <span className="ml-3 font-medium">{file.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${fileService.getCategoryColor(file.category)}`}
                      >
                        {file.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {fileService.formatFileSize(file.file_size)}
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(file.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleDownload(file)}
                          className="p-1 hover:bg-gray-100 rounded"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button className="p-1 hover:bg-gray-100 rounded" title="Share">
                          <Share2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Folders */}
            {folders
              .filter(f => f.parent_id === (currentFolder?._id || null))
              .map(folder => (
                <div
                  key={folder._id}
                  onClick={() => navigateToFolder(folder)}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md cursor-pointer transition-shadow"
                >
                  <div className="flex flex-col items-center">
                    <FolderOpen className="h-12 w-12 text-yellow-500 mb-2" />
                    <span className="text-sm font-medium text-center truncate w-full">
                      {folder.name}
                    </span>
                    <span className="text-xs text-gray-500">{folder.file_count} items</span>
                  </div>
                </div>
              ))}
            {/* Files */}
            {files.map(file => (
              <div
                key={file._id}
                className={`bg-white border rounded-lg p-4 hover:shadow-md cursor-pointer transition-shadow ${
                  selectedFiles.includes(file._id)
                    ? 'border-brand-500 ring-2 ring-brand-200'
                    : 'border-gray-200'
                }`}
                onClick={() => toggleFileSelection(file._id)}
              >
                <div className="flex flex-col items-center">
                  <div className="h-12 w-12 flex items-center justify-center mb-2">
                    {getFileIcon(file)}
                  </div>
                  <span className="text-sm font-medium text-center truncate w-full">
                    {file.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    {fileService.formatFileSize(file.file_size)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalFiles}
              itemsPerPage={filters.limit || 20}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </ModuleLayout>

      {/* Upload Drawer */}
      {showUploadDrawer &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
              onClick={() => !uploading && setShowUploadDrawer(false)}
            />
            <div className="fixed top-6 right-6 bottom-6 z-[100] w-[500px] animate-in slide-in-from-right">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <Upload className="h-5 w-5 text-brand-600" />
                    <h2 className="text-lg font-semibold">Upload Files</h2>
                  </div>
                  <button
                    onClick={() => !uploading && setShowUploadDrawer(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                    disabled={uploading}
                  >
                    <span className="sr-only">Close</span>×
                  </button>
                </div>

                <div className="flex-1 p-6">
                  <div
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand-500 transition-colors"
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault();
                      handleFileUpload(e.dataTransfer.files);
                    }}
                  >
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">
                      Drag and drop files here, or click to browse
                    </p>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      id="file-upload"
                      onChange={e => e.target.files && handleFileUpload(e.target.files)}
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg cursor-pointer hover:bg-brand-700"
                    >
                      Browse Files
                    </label>
                  </div>

                  {uploading && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-brand-600 transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {currentFolder && (
                    <p className="mt-4 text-sm text-gray-500">
                      Files will be uploaded to: <strong>{currentFolder.name}</strong>
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body
        )}

      {/* Create Folder Modal */}
      {showCreateFolderModal &&
        createPortal(
          <CreateFolderModal
            projectId={projectId || ''}
            parentId={currentFolder?._id}
            onClose={() => setShowCreateFolderModal(false)}
            onCreated={() => {
              setShowCreateFolderModal(false);
              loadData();
            }}
          />,
          document.body
        )}
    </>
  );
};

// Create Folder Modal Component
interface CreateFolderModalProps {
  projectId: string;
  parentId?: string;
  onClose: () => void;
  onCreated: () => void;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  projectId,
  parentId,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    try {
      const result = await fileService.createFolder(projectId, {
        name: name.trim(),
        description: description.trim() || undefined,
        parent_id: parentId,
      });

      if (result.success) {
        onCreated();
      }
    } catch (error) {
      console.error('Failed to create folder:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100] w-[400px]">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <FolderPlus className="h-5 w-5 text-brand-600" />
              <h2 className="text-lg font-semibold">New Folder</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Folder Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Enter folder name"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                placeholder="Enter description"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !name.trim()}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Folder'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Files;
